import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  Assignment,
  Quiz,
  Schedule,
  Person,
  TrendingUp,
  Settings,
  PlayArrow,
  CheckCircle,
  RadioButtonUnchecked,
  MoreVert,
  ExpandMore,
  VideoLibrary,
  Description,
  Link as LinkIcon,
  School,
  Timer,
  Star,
  Upload,
  Download,
  Edit,
  Send,
  Save,
  Refresh,
} from '@mui/icons-material';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
}

interface Assignment {
  id: number;
  title: string;
  description: string;
  type: 'homework' | 'project' | 'essay' | 'quiz' | 'presentation';
  subject: string;
  topic: string;
  dueDate: string;
  status: 'not_started' | 'in_progress' | 'submitted' | 'graded';
  points: number;
  instructions: string;
  attachments: Attachment[];
  questions?: AssignmentQuestion[];
  submission?: AssignmentSubmission;
  grade?: AssignmentGrade;
}

interface Attachment {
  id: number;
  name: string;
  type: 'document' | 'image' | 'video' | 'link';
  url: string;
  size?: string;
}

interface AssignmentQuestion {
  id: number;
  question: string;
  type: 'text' | 'multiple_choice' | 'file_upload' | 'essay';
  required: boolean;
  options?: string[];
  maxLength?: number;
  fileTypes?: string[];
}

interface AssignmentSubmission {
  id: number;
  submittedAt: string;
  answers: SubmissionAnswer[];
  attachments: Attachment[];
  status: 'draft' | 'submitted';
}

interface SubmissionAnswer {
  questionId: number;
  answer: string;
  attachments?: Attachment[];
}

interface AssignmentGrade {
  score: number;
  maxScore: number;
  feedback: string;
  gradedAt: string;
  gradedBy: string;
}

const Assignments: React.FC = () => {
  const { courseId, subjectId } = useParams<{ courseId: string; subjectId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [submissionAnswers, setSubmissionAnswers] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Mock data for now - replace with API call later
    setTimeout(() => {
      const mockAssignments: Assignment[] = [
        {
          id: 1,
          title: 'Linear Equations Practice Problems',
          description: 'Complete the following linear equation problems and show your work.',
          type: 'homework',
          subject: 'Algebra',
          topic: 'Linear Equations',
          dueDate: '2024-01-20',
          status: 'in_progress',
          points: 50,
          instructions: 'Solve each equation step by step. Show all your work clearly. Submit your answers in the text boxes provided.',
          attachments: [
            {
              id: 1,
              name: 'Linear Equations Worksheet.pdf',
              type: 'document',
              url: '#',
              size: '2.3 MB'
            }
          ],
          questions: [
            {
              id: 1,
              question: 'Solve for x: 2x + 5 = 13',
              type: 'text',
              required: true
            },
            {
              id: 2,
              question: 'Solve for y: 3y - 7 = 14',
              type: 'text',
              required: true
            },
            {
              id: 3,
              question: 'Solve the system of equations: x + y = 10, 2x - y = 4',
              type: 'text',
              required: true
            },
            {
              id: 4,
              question: 'Upload your work for the above problems',
              type: 'file_upload',
              required: false,
              fileTypes: ['pdf', 'jpg', 'png']
            }
          ],
          submission: {
            id: 1,
            submittedAt: '',
            answers: [],
            attachments: [],
            status: 'draft'
          }
        },
        {
          id: 2,
          title: 'Quadratic Functions Project',
          description: 'Create a presentation about quadratic functions and their real-world applications.',
          type: 'project',
          subject: 'Algebra',
          topic: 'Quadratic Functions',
          dueDate: '2024-01-25',
          status: 'not_started',
          points: 100,
          instructions: 'Research and create a 5-minute presentation about quadratic functions. Include at least 3 real-world examples.',
          attachments: [
            {
              id: 2,
              name: 'Project Guidelines.pdf',
              type: 'document',
              url: '#',
              size: '1.8 MB'
            }
          ],
          questions: [
            {
              id: 5,
              question: 'Describe your chosen real-world applications of quadratic functions',
              type: 'essay',
              required: true,
              maxLength: 500
            },
            {
              id: 6,
              question: 'Upload your presentation file',
              type: 'file_upload',
              required: true,
              fileTypes: ['ppt', 'pptx', 'pdf']
            }
          ]
        },
        {
          id: 3,
          title: 'Algebra Quiz - Chapter 1',
          description: 'Multiple choice quiz covering linear equations and basic algebra concepts.',
          type: 'quiz',
          subject: 'Algebra',
          topic: 'Linear Equations',
          dueDate: '2024-01-18',
          status: 'graded',
          points: 25,
          instructions: 'Answer all questions. You have 30 minutes to complete this quiz.',
          questions: [
            {
              id: 7,
              question: 'What is the solution to 2x + 3 = 11?',
              type: 'multiple_choice',
              required: true,
              options: ['x = 4', 'x = 7', 'x = 8', 'x = 14']
            },
            {
              id: 8,
              question: 'Which of the following is a linear equation?',
              type: 'multiple_choice',
              required: true,
              options: ['x² + 2x = 5', '2x + 3 = 7', 'x³ - 1 = 0', '√x = 4']
            }
          ],
          submission: {
            id: 2,
            submittedAt: '2024-01-17T14:30:00Z',
            answers: [
              { questionId: 7, answer: 'x = 4' },
              { questionId: 8, answer: '2x + 3 = 7' }
            ],
            attachments: [],
            status: 'submitted'
          },
          grade: {
            score: 20,
            maxScore: 25,
            feedback: 'Good work! You got most questions correct. Review the concept of linear equations for the one you missed.',
            gradedAt: '2024-01-18T09:15:00Z',
            gradedBy: 'Mike Johnson'
          }
        }
      ];
      setAssignments(mockAssignments);
      setLoading(false);
    }, 1000);
  }, [courseId, subjectId]);

  const handleBackToSubject = () => {
    navigate(`/student-courses/${courseId}/subjects/${subjectId}`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleAssignmentClick = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
  };

  const handleAnswerChange = (questionId: number, answer: string) => {
    setSubmissionAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmitAssignment = () => {
    // TODO: Submit assignment
    console.log('Submit assignment:', selectedAssignment?.id, submissionAnswers);
  };

  const handleSaveDraft = () => {
    // TODO: Save draft
    console.log('Save draft:', selectedAssignment?.id, submissionAnswers);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'graded':
        return 'success';
      case 'submitted':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'not_started':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'homework':
        return <Assignment color="primary" />;
      case 'project':
        return <School color="secondary" />;
      case 'quiz':
        return <Quiz color="warning" />;
      case 'essay':
        return <Description color="info" />;
      case 'presentation':
        return <VideoLibrary color="error" />;
      default:
        return <Assignment color="default" />;
    }
  };

  const filteredAssignments = assignments.filter(assignment => {
    switch (selectedTab) {
      case 0: return assignment.status === 'not_started' || assignment.status === 'in_progress';
      case 1: return assignment.status === 'submitted';
      case 2: return assignment.status === 'graded';
      default: return true;
    }
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Please log in to access assignments.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate('/student-dashboard')}
            sx={{ cursor: 'pointer' }}
          >
            Dashboard
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate(`/student-courses/${courseId}`)}
            sx={{ cursor: 'pointer' }}
          >
            My Courses
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={() => navigate(`/student-courses/${courseId}`)}
            sx={{ cursor: 'pointer' }}
          >
            Mathematics
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={handleBackToSubject}
            sx={{ cursor: 'pointer' }}
          >
            Algebra
          </Link>
          <Typography color="text.primary">Assignments</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <IconButton onClick={handleBackToSubject} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              Assignments
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Complete your assignments and track your progress
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Assignments List */}
        <Grid item xs={12} md={selectedAssignment ? 6 : 12}>
          <Card>
            <CardContent>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                  <Tab label="Active" />
                  <Tab label="Submitted" />
                  <Tab label="Graded" />
                </Tabs>
              </Box>
              
              <List>
                {filteredAssignments.map((assignment, index) => (
                  <React.Fragment key={assignment.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                        mb: 1,
                      }}
                      onClick={() => handleAssignmentClick(assignment)}
                    >
                      <ListItemIcon>
                        {getTypeIcon(assignment.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{assignment.title}</Typography>
                            <Chip
                              label={assignment.status.replace('_', ' ')}
                              size="small"
                              color={getStatusColor(assignment.status) as any}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {assignment.description}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="caption">
                                Due: {assignment.dueDate}
                              </Typography>
                              <Typography variant="caption">
                                Points: {assignment.points}
                              </Typography>
                              {assignment.grade && (
                                <Typography variant="caption" color="success.main">
                                  Grade: {assignment.grade.score}/{assignment.grade.maxScore}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < filteredAssignments.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Assignment Details */}
        {selectedAssignment && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{selectedAssignment.title}</Typography>
                  <IconButton onClick={() => setSelectedAssignment(null)}>
                    <MoreVert />
                  </IconButton>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedAssignment.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Instructions:
                  </Typography>
                  <Typography variant="body2">
                    {selectedAssignment.instructions}
                  </Typography>
                </Box>

                {selectedAssignment.attachments.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Attachments:
                    </Typography>
                    <List dense>
                      {selectedAssignment.attachments.map((attachment) => (
                        <ListItem key={attachment.id} sx={{ py: 0 }}>
                          <ListItemIcon>
                            <Description />
                          </ListItemIcon>
                          <ListItemText
                            primary={attachment.name}
                            secondary={attachment.size}
                          />
                          <Button size="small" startIcon={<Download />}>
                            Download
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {selectedAssignment.questions && selectedAssignment.questions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Questions:
                    </Typography>
                    {selectedAssignment.questions.map((question, index) => (
                      <Box key={question.id} sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          {index + 1}. {question.question}
                          {question.required && <span style={{ color: 'red' }}> *</span>}
                        </Typography>
                        
                        {question.type === 'text' && (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            placeholder="Enter your answer..."
                            value={submissionAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          />
                        )}

                        {question.type === 'essay' && (
                          <TextField
                            fullWidth
                            multiline
                            rows={6}
                            variant="outlined"
                            placeholder="Enter your essay..."
                            value={submissionAnswers[question.id] || ''}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            inputProps={{ maxLength: question.maxLength }}
                            helperText={`${(submissionAnswers[question.id] || '').length}/${question.maxLength} characters`}
                          />
                        )}

                        {question.type === 'multiple_choice' && (
                          <FormControl component="fieldset">
                            <RadioGroup
                              value={submissionAnswers[question.id] || ''}
                              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            >
                              {question.options?.map((option, optionIndex) => (
                                <FormControlLabel
                                  key={optionIndex}
                                  value={option}
                                  control={<Radio />}
                                  label={option}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        )}

                        {question.type === 'file_upload' && (
                          <Box>
                            <Button
                              variant="outlined"
                              startIcon={<Upload />}
                              component="label"
                            >
                              Upload File
                              <input
                                type="file"
                                hidden
                                accept={question.fileTypes?.map(type => `.${type}`).join(',')}
                              />
                            </Button>
                            {question.fileTypes && (
                              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                Accepted formats: {question.fileTypes.join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {selectedAssignment.grade && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Grade: {selectedAssignment.grade.score}/{selectedAssignment.grade.maxScore}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Feedback: {selectedAssignment.grade.feedback}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Graded by {selectedAssignment.grade.gradedBy} on {new Date(selectedAssignment.grade.gradedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {selectedAssignment.status === 'not_started' || selectedAssignment.status === 'in_progress' ? (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<Send />}
                        onClick={handleSubmitAssignment}
                      >
                        Submit Assignment
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Save />}
                        onClick={handleSaveDraft}
                      >
                        Save Draft
                      </Button>
                    </>
                  ) : selectedAssignment.status === 'submitted' ? (
                    <Button variant="outlined" disabled>
                      Submitted
                    </Button>
                  ) : (
                    <Button variant="outlined" startIcon={<Download />}>
                      Download Feedback
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Assignments;
